import { useEffect, useState } from "react";
import { DialogProps, TimeRange } from "../../types/dialog-types";
import { tagSelectStyles as s } from "./tagSelectDialogStyles";
import { getTigerResource } from "../../utils/tigerResource";
import LangDateInput from "../LangDateInput";
import { getEventModelTimes } from "../../api/getEventModelTimes";
import {
  dateTimeToTigerSeconds,
  formatDurationSecs,
  formatTigerDateTime,
  tigerSecondsToDate,
  validateEndAfterStart,
  validateMaxDuration,
} from "../../utils/tigerTime";
import { useTigerMaxReplay } from "../../utils/tigerMaxReplay";
import {
  addEnteredReportTime,
  getEnteredReportTimes,
  getReportRange,
  setReportRange,
} from "../../utils/reportDialogState";

import { useTurbineSerials } from "../../utils/turbineSerial";

interface EventRow {
  startDate: string; // "YYYY-MM-DD" input values
  startTime: string; // "HH:MM:SS"
  endDate: string;
  endTime: string;
  startDisplay: string;
  endDisplay: string;
  duration: string;
  /** Start in Tiger seconds — keys the Serial column on serial-tracking sites. */
  startSecs: number;
}

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

function tigerSecsToInputs(tigerSecs: number): { date: string; time: string } {
  const d = tigerSecondsToDate(tigerSecs);
  return {
    date: `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`,
    time: `${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}:${pad2(d.getUTCSeconds())}`,
  };
}

// Port of report_time_shell_c (IDD_REPORT_TIME_SHELL): pick the time window an
// event-model report covers. The list offers the model's recorded occurrences
// (or, for the TIME pseudo-model, previously entered windows); clicking a row
// fills the pickers. Themed to match the Graph Channel Selection dialog.
export interface ReportTimeDialogProps extends DialogProps {
  /** Turbine config the report runs against. */
  configName?: string;
  /** The report's event model (ini id); "TIME" lists previously entered windows. */
  modelName?: string;
  /** Display name for the model row (defaults to modelName). */
  modelLabel?: string;
  onOk?: (timeRange: TimeRange) => void;
  onCancel?: () => void;
}

export function ReportTimeDialog({
  open,
  onClose,
  onApply,
  configName = "",
  modelName = "",
  modelLabel,
  onOk,
  onCancel,
}: ReportTimeDialogProps) {
  const [timeRange, setTimeRange] = useState<TimeRange>({
    startDate: "",
    startTime: "",
    endDate: "",
    endTime: "",
  });
  const [events, setEvents] = useState<EventRow[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [selectedEventIndex, setSelectedEventIndex] = useState(-1);
  const [error, setError] = useState("");
  const maxReplay = useTigerMaxReplay();
  // Serial-tracking sites show the engine serial per row instead of Duration
  // (C++ report_time_shell swaps the two columns' widths on enable_turbine_serial).
  const { enabled: serialEnabled, serialFor } = useTurbineSerials(configName, events);

  useEffect(() => {
    if (!open) return;
    setTimeRange(getReportRange());
    setSelectedEventIndex(-1);
    setError("");
    setEventsLoading(false);

    if (modelName === "TIME" || modelName === "") {
      // TIME model: re-offer the windows the user entered before.
      setEvents(
        getEnteredReportTimes().map((t) => {
          const st = dateTimeToTigerSeconds(t.startDate, t.startTime) ?? 0;
          const en = dateTimeToTigerSeconds(t.endDate, t.endTime) ?? 0;
          return {
            ...t,
            startDisplay: formatTigerDateTime(st),
            endDisplay: formatTigerDateTime(en),
            duration: formatDurationSecs(en - st),
            startSecs: st,
          };
        }),
      );
      return;
    }

    let cancelled = false;
    setEvents([]);
    setEventsLoading(true);
    (async () => {
      try {
        const occs = await getEventModelTimes(configName, "HIST", modelName);
        if (cancelled) return;
        // Time order, oldest first (the same ordering the TIME-model entered
        // list uses; the C++ time_pair sort keys on start then end).
        occs.sort((a, b) => a.start - b.start || a.end - b.end);
        setEvents(
          occs.map(({ start, end }) => {
            const st = tigerSecsToInputs(start);
            const en = tigerSecsToInputs(end);
            return {
              startDate: st.date,
              startTime: st.time,
              endDate: en.date,
              endTime: en.time,
              startDisplay: formatTigerDateTime(start),
              endDisplay: formatTigerDateTime(end),
              duration: formatDurationSecs(end - start),
              startSecs: start,
            };
          }),
        );
      } catch (e) {
        console.error("get_event_model_times failed:", e);
        if (!cancelled) {
          setEvents([]);
          // An empty list and "the server did not answer" must not look the
          // same: the backend now fails an unanswered request instead of
          // decoding it as zero events, so say so where the user can see it.
          setError(String((e as { message?: string })?.message ?? e ?? "Failed to load event times"));
        }
      } finally {
        if (!cancelled) setEventsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, configName, modelName]);

  const handleEventSelect = (index: number) => {
    setSelectedEventIndex(index);
    const ev = events[index];
    if (!ev) return;
    setTimeRange({
      startDate: ev.startDate,
      startTime: ev.startTime,
      endDate: ev.endDate,
      endTime: ev.endTime,
    });
  };

  const handleOk = () => {
    const start = dateTimeToTigerSeconds(timeRange.startDate, timeRange.startTime);
    const end = dateTimeToTigerSeconds(timeRange.endDate, timeRange.endTime);
    const rangeErr = validateEndAfterStart(start, end);
    if (start === null || end === null || rangeErr) {
      setError(rangeErr ?? getTigerResource("TIGER_ERROR_START_TIME"));
      return;
    }
    const durErr = validateMaxDuration(end - start, maxReplay);
    if (durErr) {
      setError(durErr);
      return;
    }
    setError("");
    setReportRange({ ...timeRange });
    if (modelName === "TIME") addEnteredReportTime({ ...timeRange });
    onOk?.(timeRange);
    onApply?.({ timeRange });
    onClose();
  };

  const handleCancel = () => {
    onCancel?.();
    onClose();
  };

  if (!open) return null;
  const title = getTigerResource("TIGER_REPORT_TIME_TITLE");

  return (
    <div style={s.overlay}>
      <div
        style={{
          ...s.dialog,
          width: 580,
          height: 560,
          top: "max(2.5vh, calc(50vh - 280px))",
          left: "max(2.5vw, calc(50vw - 290px))",
        }}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div style={s.header}>
          <span style={s.title}>{title}</span>
          <button style={s.closeBtn} onClick={handleCancel} aria-label={getTigerResource("TIGER_RES_CLOSE")}>
            &#x2715;
          </button>
        </div>

        <div style={s.body}>
          <div style={s.card}>
            <div style={s.fieldGroup}>
              <div style={s.field}>
                <span style={s.fieldLabel}>{getTigerResource("TIGER_CHANNEL_EVENT")}</span>
                <span style={{ fontSize: 13, color: "#111827" }}>
                  {modelLabel ?? modelName ?? ""}
                </span>
              </div>
            </div>
            <div style={s.fieldGroup}>
              <div style={s.field}>
                <label style={s.fieldLabel} htmlFor="report-time-start">
                  {getTigerResource("TIGER_RES_START_TIME")}
                </label>
                <input
                  id="report-time-start"
                  style={s.input}
                  type="time"
                  step="1"
                  aria-label={getTigerResource("TIGER_RES_START_TIME")}
                  value={timeRange.startTime}
                  onChange={(e) => setTimeRange((prev) => ({ ...prev, startTime: e.target.value }))}
                />
                <LangDateInput
                  style={s.input}
                  aria-label={getTigerResource("TIGER_RES_START_DATE")}
                  value={timeRange.startDate}
                  onChange={(iso) => setTimeRange((prev) => ({ ...prev, startDate: iso }))}
                />
              </div>
              <div style={s.field}>
                <label style={s.fieldLabel} htmlFor="report-time-end">
                  {getTigerResource("TIGER_RES_END_TIME")}
                </label>
                <input
                  id="report-time-end"
                  style={s.input}
                  type="time"
                  step="1"
                  aria-label={getTigerResource("TIGER_RES_END_TIME")}
                  value={timeRange.endTime}
                  onChange={(e) => setTimeRange((prev) => ({ ...prev, endTime: e.target.value }))}
                />
                <LangDateInput
                  style={s.input}
                  aria-label={getTigerResource("TIGER_RES_END_DATE")}
                  value={timeRange.endDate}
                  onChange={(iso) => setTimeRange((prev) => ({ ...prev, endDate: iso }))}
                />
              </div>
            </div>
          </div>

          {/* Recorded occurrences of the model */}
          <div style={s.section}>
            <div style={s.tableWrap}>
              <div className="list-control" style={{ flex: 1, minHeight: 160, overflow: "auto" }}>
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: 180 }}>{getTigerResource("TIGER_RES_START_TIME")}</th>
                      <th style={{ width: 180 }}>{getTigerResource("TIGER_RES_END_TIME")}</th>
                      <th style={{ width: 90 }}>
                        {getTigerResource(serialEnabled ? "TIGER_RES_SERIAL" : "TIGER_RES_DURATION")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map((ev, idx) => (
                      <tr
                        key={idx}
                        className={selectedEventIndex === idx ? "selected" : ""}
                        onClick={() => handleEventSelect(idx)}
                      >
                        <td>{ev.startDisplay}</td>
                        <td>{ev.endDisplay}</td>
                        <td>{serialEnabled ? serialFor(ev.startSecs) : ev.duration}</td>
                      </tr>
                    ))}
                    {eventsLoading && (
                      <tr>
                        <td colSpan={3} style={{ color: "#6b7280", fontStyle: "italic" }}>
                          {getTigerResource("TIGER_LOAD_EVENT_TIMES_LOADING")}
                        </td>
                      </tr>
                    )}
                    {!eventsLoading && events.length === 0 && (
                      <tr>
                        <td colSpan={3} style={{ color: "#6b7280" }}>
                          {getTigerResource("TIGER_REPORT_NO_TIMES")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {error && (
            <div role="alert" style={{ color: "#b00020", fontSize: 12 }}>
              {error}
            </div>
          )}
        </div>

        <div style={s.footer}>
          <button style={s.btnPrimary} onClick={handleOk}>
            {getTigerResource("TIGER_RES_OK")}
          </button>
          <button style={s.btnSecondary} onClick={handleCancel}>
            {getTigerResource("TIGER_RES_CANCEL")}
          </button>
        </div>
      </div>
    </div>
  );
}
